import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { connect } from 'react-redux'
import {
  makeStyles,
  createStyles,
  Typography,
  Grid,
  Box,
} from '@material-ui/core'
import CMSText, { prismicText } from '../Global/CMSText'
import Button from '../Button/Button'
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace'
import Loader from '../Loader/Loader'
import { Order } from '@/interfaces/common/contract'
import TakeBackOrderItem from './TakeBackOrderItem'
import { DynamicFormDocumentFlexible } from '@/interfaces/common/prismicDynamicForm'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    hide: {
      opacity: 0,
      transition: theme.transitions.create('opacity'),
    },
    show: {
      opacity: 1,
      transition: theme.transitions.create('opacity'),
    },
    takeBackListTitle: {
      marginBottom: theme.spacing(2),
    },
    takeBackListHeader: {
      display: 'flex',
      justifyContent: 'start',
      alignItems: 'center',
    },
    takeBackItemTitle: {
      paddingLeft: theme.spacing(1),
    },
    circle: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
    },
    productCard: {
      display: 'flex',
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 60,
      background: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    productCardCheckbox: {},
    productCardImg: {
      width: 50,
      height: 50,
      position: 'relative',
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginRight: theme.spacing(1),
      },
    },
    productCardContent: {
      flex: 1,
    },
    takeBackItem: {
      borderLeft: `1px solid currentColor`,
      paddingLeft: theme.spacing(2),
      marginLeft: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    takeBackItemActive: {
      borderColor: 'transparent',
      paddingLeft: 0,
      marginLeft: theme.spacing(1),
    },
    takeBackItemLast: {
      borderLeft: 0,
    },
    imgProduct: {
      width: 50,
      height: 50,
      objectFit: 'contain',
      display: 'block',
    },
    productName: {},
    productStatus: {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(1),
      },
    },
    takeBackOrder: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      width: '100%',
    },
    orderDetailsRow: {
      textAlign: 'right',
    },
    orderDetailsBtn: {},
    orderDetailsRowActions: {
      padding: theme.spacing(1, 0, 2),
    },
    simpleButton: {
      padding: 0,
      textTransform: 'inherit',
      color: theme.extraColors.greyText,
      '&:hover, &:focus': {
        background: 'none',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '.8rem',
      },
    },
    simpleButtonPrimary: {
      color: theme.palette.primary.main,
    },
    simpleButtonDanger: {
      color: theme.palette.error.main,
    },
    currentOrderList: {
      padding: theme.spacing(3),
      background: theme.palette.common.white,
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '20vh',
    },
    returnCtaContainer: {
      minHeight: '2rem',
    },
    downloadInvoiceButton: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(1.5),
    },
  })
)

type Tprops = {
  t: any
  takeback: any
  main: any
  account: any
  orders: Order[]
  user: any
  stores: any
  postProductToReturn: (data: object) => any
  getRightOfWithdrawalInfo: (id: string) => any
  handleDownloadInvoice: (orderToken: string) => any
  invoiceDownloadError: boolean
  savForm?: DynamicFormDocumentFlexible
  onOpenSavForm?: (orderNumber: string) => void
  settings?: any
}

const mapStateToProps = (state) => ({
  user: state.user,
})

const TakeBackOrderList = ({
  t,
  takeback,
  orders,
  main,
  user,
  stores,
  postProductToReturn,
  account,
  getRightOfWithdrawalInfo,
  handleDownloadInvoice,
  invoiceDownloadError,
  savForm,
  onOpenSavForm,
  settings,
}: Tprops) => {
  const classes = useStyles()

  const [activeOrder, setActiveOrder] = useState(false)
  const [activeOrderLoading, setActiveLoading] = useState(false)
  const [indexOrder, setIndexOrder] = useState(null)
  const [isModifying, setIsModifying] = useState(false)
  const [productSelection, setProductSelection] = useState(false)
  const [productToReturn, setProductToReturn] = useState([])
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [downloadLink, setDownloadLink] = useState('')
  const [productReturningReasons, setProductReturningReasons] = useState({})
  const warehouseId = stores?.selectedStore?.warehouseId
    ? stores?.selectedStore?.warehouseId
    : process.env.CUSTOM_WAREHOUSE
    ? process.env.CUSTOM_WAREHOUSE
    : null
  const [isOrderReturnable, setIsOrderReturnable] = useState([])
  const tooltipWithdrawalText = prismicText(
    takeback?.data?.order_withdrawal_text,
    t('texts:account:order_take_back_withdrawal_text')
  )

  const getReturnValues = async (idList: string[]) => {
    const responses = await Promise.all(
      idList.map((id) => {
        return getRightOfWithdrawalInfo(id).then((info) => {
          return {
            orderId: id,
            orderInfo: info,
          }
        })
      })
    )
    return responses.map((response) => {
      return {
        orderId: response.orderId,
        returnable: response?.orderInfo?.response
          ?.map((item) => item.returnable)
          .includes(true),
      }
    })
  }

  useEffect(() => {
    const ordersToCheck = orders
      .filter((order) => dayjs().diff(order.checkoutCompletedAt, 'day') <= 45)
      .map((order) => order.number)

    getReturnValues(ordersToCheck).then(
      (result) => setIsOrderReturnable(result)
      // inOrderPushWIP.push(result)
    )
  }, [orders])

  const handleActiveCurrentOrder = (orderIndex: number) => {
    if (savForm) {
      const orderNumber = orders[orderIndex]?.number || ''
      onOpenSavForm && onOpenSavForm(orderNumber)
      return
    }
    setActiveLoading(true)
    setIndexOrder(orderIndex)
    setTimeout(() => {
      setActiveOrder(true)
      setActiveLoading(false)
    }, 800)
  }

  const handleBackToOrders = () => {
    setActiveOrder(false)
    setIndexOrder(null)
    setIsModifying(false)
    setProductSelection(false)
    setProductToReturn([])
    setDownloadLink('')
    setProductReturningReasons({})
  }

  const cancelReturn = () => {
    // we reset everything
    setIsModifying(false)
    setProductSelection(false)
    setProductToReturn([])
    setProductReturningReasons({})
  }

  const handleProductSelection = (
    Sku,
    id,
    quantity,
    token,
    item,
    orderNumber?: string,
    warehouseId?: string,
    carrierId?: number
  ) => {
    setProductSelection(true)
    const productSelectionArray = [...productToReturn]

    // we first check if the product selected is already in the array of selected products
    const index = productToReturn.findIndex((product) => product.id === id)
    if (index !== -1) {
      // if it is true the we remove the row in the array and we adapt the prices
      productSelectionArray.splice(index, 1)
      // if nothing selected, disable generate button
      setProductSelection(productSelectionArray.length !== 0)

      // Remove returning reasons for this product
      const newReturningReasons = { ...productReturningReasons }
      delete newReturningReasons[id]
      setProductReturningReasons(newReturningReasons)
    } else {
      // if it is false we push a new row in the array of selected products and we adapt the prices
      productSelectionArray.push({
        id,
        Sku,
        expand: true,
        product: item.variant,
        quantity,
        orderToken: token,
        orderNumber,
        warehouseId,
        carrierId,
      })
    }
    setProductToReturn(productSelectionArray)
  }

  const handleReturningReasonChange = useCallback(
    (productId: number, returningRaisonId: number | null) => {
      setProductReturningReasons((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          returningRaisonId,
        },
      }))
    },
    []
  )

  const handleReturningReasonCommentChange = useCallback(
    (productId: number, returningComment: string) => {
      setProductReturningReasons((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          returningComment,
        },
      }))
    },
    []
  )

  return (
    <div className={classes.root}>
      <Typography
        variant={'h5'}
        component={'h2'}
        className={classes.takeBackListTitle}
      >
        <CMSText
          asText
          data={takeback?.data?.order_list_title}
          defaultText={t('texts:account:order_list_title')}
        />
      </Typography>

      {activeOrder && (
        <Grid container spacing={1} className={classes.orderDetailsRowActions}>
          <Grid item xs={3} md={6}>
            <Button
              size={'small'}
              className={classes.simpleButton}
              onClick={() => handleBackToOrders()}
              startIcon={<KeyboardBackspaceIcon />}
              disableRipple
            >
              <CMSText
                asText
                data={account?.data?.order_details_close}
                defaultText={t('texts:account:order_details_close')}
              />
            </Button>
          </Grid>
          <Grid
            item
            xs={9}
            md={6}
            alignItems={'center'}
            alignContent={'flex-end'}
          >
            <Box textAlign={'right'}>
              {isModifying ? (
                <Button
                  size={'small'}
                  className={clsx(
                    classes.simpleButton,
                    classes.simpleButtonDanger
                  )}
                  onClick={() => cancelReturn()}
                  disableRipple
                >
                  <CMSText
                    asText
                    data={account?.data?.order_details_manage_cancel}
                    defaultText={t('texts:account:order_details_manage_cancel')}
                  />
                </Button>
              ) : (
                <Button
                  size={'small'}
                  className={clsx(
                    classes.simpleButton,
                    classes.simpleButtonPrimary
                  )}
                  onClick={() => setIsModifying(true)}
                  disableRipple
                >
                  <CMSText
                    asText
                    data={account?.data?.order_details_manage}
                    defaultText={t('texts:account:order_details_manage')}
                  />
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      )}

      {orders.map((order, index) => {
        const lastOrder = index + 1 === orders.length
        const orderDate = dayjs(order.checkoutCompletedAt).format('L')

        const isDisabled = isOrderReturnable.find(
          (orderInfos) => orderInfos.orderId === order.number
        )?.returnable

        if (indexOrder === null) {
          return (
            <div key={index}>
              <TakeBackOrderItem
                takeback={takeback}
                main={main}
                account={account}
                order={order}
                activeOrder={activeOrder}
                isModifying={isModifying}
                isDisabled={isDisabled}
                isDownloadLoading={isDownloadLoading}
                downloadLink={downloadLink}
                productSelection={productSelection}
                productToReturn={productToReturn}
                productReturningReasons={productReturningReasons}
                isOrderReturnable={isOrderReturnable}
                orderDate={orderDate}
                handleProductSelection={handleProductSelection}
                handleReturningReasonChange={handleReturningReasonChange}
                handleReturningReasonCommentChange={
                  handleReturningReasonCommentChange
                }
                handleActiveCurrentOrder={handleActiveCurrentOrder}
                lastOrder={lastOrder}
                warehouseId={warehouseId}
                handleDownloadInvoice={handleDownloadInvoice}
                invoiceDownloadError={invoiceDownloadError}
                tooltipWithdrawalText={tooltipWithdrawalText}
                setIsDownloadLoading={setIsDownloadLoading}
                setDownloadLink={setDownloadLink}
                user={user}
                postProductToReturn={postProductToReturn}
                index={index}
                settings={settings}
              />
            </div>
          )
        } else {
          if (indexOrder === index) {
            return activeOrderLoading ? (
              <div className={classes.loaderContainer}>
                <Loader />
              </div>
            ) : (
              <TakeBackOrderItem
                takeback={takeback}
                main={main}
                account={account}
                order={order}
                activeOrder={activeOrder}
                isModifying={isModifying}
                isDisabled={isDisabled}
                isDownloadLoading={isDownloadLoading}
                downloadLink={downloadLink}
                productSelection={productSelection}
                productToReturn={productToReturn}
                productReturningReasons={productReturningReasons}
                isOrderReturnable={isOrderReturnable}
                orderDate={orderDate}
                handleProductSelection={handleProductSelection}
                handleReturningReasonChange={handleReturningReasonChange}
                handleReturningReasonCommentChange={
                  handleReturningReasonCommentChange
                }
                handleActiveCurrentOrder={handleActiveCurrentOrder}
                lastOrder={lastOrder}
                warehouseId={warehouseId}
                handleDownloadInvoice={handleDownloadInvoice}
                invoiceDownloadError={invoiceDownloadError}
                tooltipWithdrawalText={tooltipWithdrawalText}
                setIsDownloadLoading={setIsDownloadLoading}
                setDownloadLink={setDownloadLink}
                user={user}
                postProductToReturn={postProductToReturn}
                index={index}
                settings={settings}
              />
            )
          }
        }
      })}
    </div>
  )
}

export default connect(mapStateToProps, null)(TakeBackOrderList)
